/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Wizard Step 4: Context Chaining Generation & Progress Tracker View (Task 10.2.4 & 10.7.2)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useEffect, useRef, useState } from 'react';
import {
  useLongformWizardStore,
} from '../../stores/useLongformWizardStore';
import { useEditorStore } from '../../stores/useEditorStore';
import { useI18n } from '../../stores/useI18nStore';
import { longformGenerationService } from '../../services/longform-generation.service';
import {
  Flame,
  Play,
  Pause,
  RotateCcw,
  CheckCircle2,
  Clock,
  Sparkles,
  FileText,
  Copy,
  Download,
  ArrowLeft,
  Check,
  StopCircle,
  FileEdit,
} from 'lucide-react';

interface Step4GenerationViewProps {
  onPushToEditor?: () => void;
}

export const Step4GenerationView: React.FC<Step4GenerationViewProps> = ({
  onPushToEditor,
}) => {
  const {
    headings,
    generation,
    setStep,
    getSelectedFinalTitle,
  } = useLongformWizardStore();

  const { setTitle: setEditorTitle, setContent: setEditorContent } = useEditorStore();
  const { t } = useI18n();

  const [copied, setCopied] = useState(false);
  const streamViewportRef = useRef<HTMLDivElement>(null);

  // Auto-start generation when entering Step 4 if not started yet
  useEffect(() => {
    if (
      !generation.isGenerating &&
      generation.progressPercent === 0 &&
      headings.length > 0 &&
      !generation.fullArticleMarkdown
    ) {
      void longformGenerationService.startGeneration();
    }
  }, [headings.length, generation.isGenerating, generation.progressPercent, generation.fullArticleMarkdown]);

  // Auto-scroll viewport down during generation
  useEffect(() => {
    if (generation.isGenerating && streamViewportRef.current) {
      streamViewportRef.current.scrollTop = streamViewportRef.current.scrollHeight;
    }
  }, [generation.isGenerating, generation.generatedSections]);

  const handlePauseResume = () => {
    if (generation.isPaused) {
      longformGenerationService.resume();
    } else {
      longformGenerationService.pause();
    }
  };

  const handleStop = () => {
    longformGenerationService.abort();
  };

  const handleRestart = () => {
    void longformGenerationService.startGeneration();
  };

  const handleCopyMarkdown = async () => {
    if (!generation.fullArticleMarkdown) return;
    try {
      await navigator.clipboard.writeText(generation.fullArticleMarkdown);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.warn('Failed to copy markdown to clipboard:', err);
    }
  };

  const handleDownloadMarkdown = () => {
    const title = getSelectedFinalTitle() || 'longform-article';
    const blob = new Blob([generation.fullArticleMarkdown], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${title.toLowerCase().replace(/\s+/g, '-')}.md`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleTransferToTiptap = () => {
    const title = getSelectedFinalTitle();
    if (title) {
      setEditorTitle(title);
    }

    if (generation.fullArticleMarkdown) {
      const htmlParagraphs = generation.fullArticleMarkdown
        .split('\n\n')
        .map((chunk) => {
          if (chunk.startsWith('# ')) {
            return `<h1>${chunk.replace(/^# /, '')}</h1>`;
          }
          if (chunk.startsWith('## ')) {
            return `<h2>${chunk.replace(/^## /, '')}</h2>`;
          }
          if (chunk.startsWith('### ')) {
            return `<h3>${chunk.replace(/^### /, '')}</h3>`;
          }
          if (chunk.startsWith('- ')) {
            const listItems = chunk
              .split('\n')
              .map((li) => `<li>${li.replace(/^- /, '')}</li>`)
              .join('');
            return `<ul>${listItems}</ul>`;
          }
          return `<p>${chunk}</p>`;
        })
        .join('');

      setEditorContent(htmlParagraphs);
    }

    if (onPushToEditor) {
      onPushToEditor();
    }
  };

  const isCompleted = generation.progressPercent === 100 && !generation.isGenerating;
  const articleTitle = getSelectedFinalTitle() || t('editor.untitled');

  return (
    <div className="space-y-6 animate-fade-in max-w-5xl mx-auto">
      {/* 1. Generation Header & Progress Status */}
      <div className="p-6 rounded-2xl border border-surface-border bg-surface shadow-glass space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <Flame className={`w-5 h-5 ${generation.isGenerating ? 'text-accent animate-pulse' : 'text-primary'}`} />
              <h3 className="font-heading font-extrabold text-lg text-foreground">
                {isCompleted ? t('wizard.step4Details.generationComplete') : t('wizard.step4Details.sectionTitle')}
              </h3>
              {generation.isGenerating && (
                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-accent/15 text-accent border border-accent/20 animate-pulse">
                  Streaming Active
                </span>
              )}
            </div>
            <p className="text-xs text-foreground-muted truncate max-w-xl">
              {articleTitle}
            </p>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {generation.isGenerating && (
              <>
                <button
                  type="button"
                  onClick={handlePauseResume}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-surface-border bg-surface-elevated text-xs font-bold hover:bg-muted text-foreground transition-colors"
                >
                  {generation.isPaused ? (
                    <>
                      <Play className="w-3.5 h-3.5 text-emerald-500" />
                      <span>{t('wizard.step4Details.resumeBtn')}</span>
                    </>
                  ) : (
                    <>
                      <Pause className="w-3.5 h-3.5 text-amber-500" />
                      <span>{t('wizard.step4Details.pauseBtn')}</span>
                    </>
                  )}
                </button>

                <button
                  type="button"
                  onClick={handleStop}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-red-500/30 bg-red-500/10 text-red-500 text-xs font-bold hover:bg-red-500/20 transition-colors"
                  title={t('wizard.step4Details.stopBtn')}
                >
                  <StopCircle className="w-3.5 h-3.5" />
                  <span>{t('wizard.step4Details.stopBtn')}</span>
                </button>
              </>
            )}

            {!generation.isGenerating && !isCompleted && (
              <button
                type="button"
                onClick={handleRestart}
                className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-primary text-primary-fg text-xs font-bold hover:bg-primary-hover shadow-glow-primary/20"
              >
                <Play className="w-3.5 h-3.5" />
                <span>{t('wizard.step4Details.restartBtn')}</span>
              </button>
            )}

            {isCompleted && (
              <button
                type="button"
                onClick={handleRestart}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-surface-border bg-surface-elevated text-xs font-bold hover:bg-muted text-foreground-muted hover:text-foreground transition-colors"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                <span>{t('wizard.step4Details.restartBtn')}</span>
              </button>
            )}
          </div>
        </div>

        {/* Animated Progress Bar */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between text-xs">
            <span className="text-foreground-muted font-medium">
              {generation.statusMessage}
            </span>
            <span className="font-mono font-bold text-primary">
              {generation.progressPercent}%
            </span>
          </div>

          <div className="w-full h-2.5 rounded-full bg-surface-elevated overflow-hidden border border-surface-border">
            <div
              className={`h-full rounded-full transition-all duration-300 ${
                isCompleted
                  ? 'bg-emerald-500'
                  : 'bg-gradient-to-r from-primary to-accent animate-pulse'
              }`}
              style={{ width: `${generation.progressPercent}%` }}
            />
          </div>
        </div>

        {/* Stream Metrics Indicator */}
        <div className="flex flex-wrap items-center justify-between gap-3 pt-1 text-xs">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1.5 text-foreground-muted">
              <Sparkles className="w-3.5 h-3.5 text-primary" />
              <span>
                {t('wizard.step4Details.generatedWords', {
                  count: (generation.totalWordsGenerated ?? generation.totalWords ?? 0).toLocaleString(),
                })}
              </span>
            </div>

            <div className="flex items-center gap-1.5 text-foreground-muted border-l border-surface-border pl-4">
              <Clock className="w-3.5 h-3.5" />
              <span>
                {Math.ceil((generation.totalWordsGenerated ?? generation.totalWords ?? 0) / 200)} {t('editor.words')}
              </span>
            </div>
          </div>

          <div className="text-[11px] text-foreground-muted">
            <span>Context Chaining Pipeline</span>
          </div>
        </div>
      </div>

      {/* 2. Interactive Split Viewport (Sections Outline on Left + Live Stream on Right) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        {/* Left Column: Heading Sections Status Tracker (4 cols) */}
        <div className="lg:col-span-4 p-4 rounded-2xl border border-surface-border bg-surface shadow-glass space-y-3">
          <div className="flex items-center justify-between pb-2 border-b border-surface-border">
            <h4 className="text-xs font-heading font-bold text-foreground uppercase tracking-wider">
              {t('wizard.step3')}
            </h4>
            <span className="text-[10px] font-mono text-foreground-muted">
              {headings.length} headings
            </span>
          </div>

          <div className="space-y-1.5 max-h-[480px] overflow-y-auto pr-1">
            {headings.map((h, idx) => {
              const isCurrent = generation.isGenerating && generation.currentSectionIndex === idx;
              const hasContent = Boolean(generation.generatedSections[h.id]);
              const isDone = isCompleted || hasContent;

              return (
                <div
                  key={h.id}
                  className={`p-2.5 rounded-xl border text-xs transition-all flex items-start gap-2.5 ${
                    isCurrent
                      ? 'border-primary bg-primary/10 shadow-glow-primary/20 ring-1 ring-primary/40'
                      : isDone
                      ? 'border-emerald-500/20 bg-emerald-500/5'
                      : 'border-surface-border/60 bg-surface-elevated/40 opacity-70'
                  } ${h.level === 3 ? 'ml-4' : ''}`}
                >
                  <div className="mt-0.5 flex-shrink-0">
                    {isCurrent ? (
                      <div className="w-4 h-4 rounded-full border-2 border-primary border-t-transparent animate-spin" />
                    ) : isDone ? (
                      <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                    ) : (
                      <Clock className="w-4 h-4 text-foreground-muted/50" />
                    )}
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <span className="text-[9px] font-mono font-bold uppercase text-foreground-muted">
                        {h.level === 3 ? 'H3' : 'H2'}
                      </span>
                      <span className="font-heading font-semibold text-foreground truncate block">
                        {h.title}
                      </span>
                    </div>

                    {hasContent && (
                      <span className="text-[10px] font-mono text-foreground-muted">
                        ~{generation.generatedSections[h.id]?.split(/\s+/).filter(Boolean).length || 0} {t('editor.words')}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Right Column: Live Streaming Article Viewport (8 cols) */}
        <div className="lg:col-span-8 flex flex-col rounded-2xl border border-surface-border bg-surface shadow-glass overflow-hidden">
          {/* Viewport Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-surface-border bg-surface-elevated">
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-primary" />
              <span className="text-xs font-bold text-foreground font-heading">
                Live Markdown Preview
              </span>
            </div>

            {/* Quick Copy / Download Buttons */}
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={!generation.fullArticleMarkdown}
                onClick={handleCopyMarkdown}
                className="flex items-center gap-1 px-2.5 py-1 rounded-lg border border-surface-border bg-surface text-[11px] font-semibold hover:bg-muted text-foreground transition-colors disabled:opacity-40"
                title={t('wizard.step4Details.copyMarkdownBtn')}
              >
                {copied ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
                <span>{copied ? t('common.copied') : t('wizard.step4Details.copyMarkdownBtn')}</span>
              </button>

              <button
                type="button"
                disabled={!generation.fullArticleMarkdown}
                onClick={handleDownloadMarkdown}
                className="flex items-center gap-1 px-2.5 py-1 rounded-lg border border-surface-border bg-surface text-[11px] font-semibold hover:bg-muted text-foreground transition-colors disabled:opacity-40"
                title={t('wizard.step4Details.downloadMarkdownBtn')}
              >
                <Download className="w-3.5 h-3.5" />
                <span>{t('wizard.step4Details.downloadMarkdownBtn')}</span>
              </button>
            </div>
          </div>

          {/* Viewport Content */}
          <div
            ref={streamViewportRef}
            className="p-6 h-[480px] overflow-y-auto space-y-4 font-mono text-xs sm:text-sm leading-relaxed text-foreground select-text"
          >
            {Object.keys(generation.generatedSections).length === 0 && !generation.isGenerating ? (
              <div className="h-full flex flex-col items-center justify-center text-center text-foreground-muted space-y-3">
                <Flame className="w-10 h-10 text-primary opacity-40 animate-pulse" />
                <p className="text-xs max-w-sm">
                  {t('wizard.step4Details.sectionDesc')}
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                {headings.map((h) => {
                  const content = generation.generatedSections[h.id];
                  if (!content) return null;

                  return (
                    <div key={h.id} className="space-y-2 animate-fade-in border-b border-surface-border/40 pb-4">
                      <div className="whitespace-pre-wrap font-sans text-xs sm:text-sm leading-relaxed text-foreground">
                        {content}
                      </div>
                    </div>
                  );
                })}

                {/* Blinking Cursor while generating */}
                {generation.isGenerating && (
                  <span className="inline-block w-2 h-4 bg-primary animate-pulse ml-1 align-middle" />
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 3. Completion Banner & Final CTAs */}
      {isCompleted && (
        <div className="p-6 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 shadow-glass flex flex-col sm:flex-row items-center justify-between gap-4 animate-scale-in">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-500 text-white flex items-center justify-center flex-shrink-0 shadow-sm">
              <Sparkles className="w-5 h-5" />
            </div>
            <div>
              <h4 className="font-heading font-extrabold text-base text-foreground">
                {t('wizard.step4Details.generationComplete')}
              </h4>
              <p className="text-xs text-foreground-muted">
                {t('wizard.step4Desc')}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 w-full sm:w-auto">
            {onPushToEditor && (
              <button
                type="button"
                onClick={handleTransferToTiptap}
                className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-primary text-primary-fg text-xs font-bold hover:bg-primary-hover shadow-glow-primary/20 transition-all active:scale-95 cursor-pointer"
              >
                <FileEdit className="w-4 h-4" />
                <span>{t('wizard.step4Details.openInEditorBtn')}</span>
              </button>
            )}
          </div>
        </div>
      )}

      {/* 4. Navigation Footer */}
      <div className="flex items-center justify-between gap-3 pt-2">
        <button
          type="button"
          onClick={() => setStep(3)}
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-surface-border bg-surface-elevated text-xs font-bold hover:bg-muted text-foreground transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>{t('common.back')}</span>
        </button>
      </div>
    </div>
  );
};
